import { DuplicateBaseIcon } from '@tableau/icons/DuplicateBaseIcon';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import * as clipboard from 'clipboard-polyfill';
import React from 'react';

import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { Tableau } from '~src/commonInfra/utils/TableauLog';
import { GenerativeAIOnPremSectionStyles } from '~src/settings/general-settings/styles/tableau-gai/GenerativeAIOnPremSectionStyles';

import { Messages } from '../../../../../codegen/Localize';

export interface ErrorDetail {
  message: string;
  statusCode?: number;
  statusText?: string;
  provider?: string;
  timestamp: string;
  rawResponse?: string;
}

export function formatRawResponse(rawResponse: string): string {
  try {
    const reviver = (_key: string, value: unknown): unknown => {
      if (typeof value !== 'string') return value;
      const trimmed = value.trim();
      if (trimmed[0] !== '{' && trimmed[0] !== '[') return value;
      try {
        return JSON.parse(trimmed, reviver);
      } catch {
        return value;
      }
    };
    return JSON.stringify(JSON.parse(rawResponse, reviver), null, 2);
  } catch {
    return rawResponse;
  }
}

export function formatErrorDetails(errorDetail: ErrorDetail): string {
  const details: string[] = [];

  if (errorDetail.provider) {
    details.push(`${Messages.onPremGAIErrorDetailsProviderLabel()} ${errorDetail.provider}`);
  }

  if (errorDetail.statusCode) {
    details.push(`${Messages.onPremGAIErrorDetailsStatusCodeLabel()} ${errorDetail.statusCode} ${errorDetail.statusText || ''}`);
  }

  details.push(`${Messages.onPremGAIErrorDetailsErrorMessageLabel()} ${errorDetail.message}`);
  details.push(`${Messages.onPremGAIErrorDetailsTimestampLabel()} ${new Date(errorDetail.timestamp).toLocaleString()}`);

  if (errorDetail.rawResponse) {
    details.push(`\n${Messages.onPremGAIErrorDetailsRawResponseLabel()}\n${formatRawResponse(errorDetail.rawResponse)}`);
  }

  return details.join('\n\n');
}

const ErrorDetailsModal: React.FC<{ errorDetail: ErrorDetail, onClose: () => void }> = ({ errorDetail, onClose }) => {
  const okButtonRef = React.useRef<HTMLButtonElement>(null);

  const footerContent = (
    <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalFooter}>
      <ButtonWidget buttonType={ButtonType.Outline} buttonRef={okButtonRef} onClick={onClose}>
        {Messages.ok()}
      </ButtonWidget>
    </div>
  );

  const handleCopy = () => {
    clipboard.writeText(formatErrorDetails(errorDetail))
      .then(() => {
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.onPremGAIErrorDetailsCopiedMessage() });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        Tableau.Logger().warn(`ErrorDetailsModal: clipboard write failed: ${message}`);
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.onPremGAIErrorDetailsCopyFailedMessage() });
      });
  };

  return (
    <DialogWidget
      title={Messages.onPremGAIErrorDetailsDialogTitle()}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Centered}
      dialogWidth={TabDialogWidth.width375px}
      dialogHeight={GenerativeAIOnPremSectionStyles.ERROR_DETAILS_MODAL_HEIGHT}
      // Focus OK on open; the default first focusable element is the copy button, which is hidden until hovered.
      disableAutoFocus={() => okButtonRef.current!}
      onCloseRequest={onClose}
      showCloseButton={true}
      showFooter={true}
      footerContent={footerContent}
      isModal={true}
    >
      <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalBody}>
        <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalMessage}>
          {Messages.onPremGAIErrorDetailsDialogMessage()}
        </div>
        <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalLabel}>
          {Messages.onPremGAIErrorDetailsLabel()}
        </div>
        <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalScrollArea}>
          <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalCopyButtonRow}>
            <ButtonWidget
              buttonType={ButtonType.LowEmphasis}
              className={GenerativeAIOnPremSectionStyles.errorDetailsModalCopyButton}
              testId="error-details-copy-button"
              title={Messages.onPremGAIErrorDetailsCopyButtonLabel()}
              aria-label={Messages.onPremGAIErrorDetailsCopyButtonLabel()}
              onClick={handleCopy}
            >
              <DuplicateBaseIcon size={14} alt="" />
            </ButtonWidget>
          </div>
          <div className={GenerativeAIOnPremSectionStyles.errorDetailsModalText}>
            {formatErrorDetails(errorDetail)}
          </div>
        </div>
      </div>
    </DialogWidget>
  );
};

export function openErrorDetailsModal(errorDetail: ErrorDetail): void {
  const modalInstance = ReactModalService.openCustom(({ close }) => (
    <ErrorDetailsModal errorDetail={errorDetail} onClose={close} />
  ));

  modalInstance.result.catch(() => {});
}
